import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Upload, ShieldCheck, Sparkles, X, FileText, CreditCard, Banknote, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { PublicAPI } from "@/lib/api";
import { koperasiInfo } from "@/lib/dummyData";
import { getMidtransConfig } from "@/lib/midtrans";
import { payRegistration } from "@/lib/registrationPayment";

const DRAFT_KEY = "kd_register_draft";

const steps = [
  { key: "identitas", label: "Identitas" },
  { key: "kontak", label: "Kontak" },
  { key: "pekerjaan", label: "Pekerjaan" },
  { key: "dokumen", label: "Dokumen" },
  { key: "pembayaran", label: "Pembayaran" },
  { key: "review", label: "Review" },
];

export default function Register() {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const { register, apiError } = useAuth();
  const [form, setForm] = useState({ name: "", nik: "", email: "", phone_number: "", password: "", partner_organization_id: "", referral_code: "", province_id: "", regency_id: "", cabang_id: "", ranting_id: "", postal_code: "", place_of_birth: "", business_name: "" });
  const [partners, setPartners] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [regencies, setRegencies] = useState([]);
  const [cabangList, setCabangList] = useState([]);
  const [rantingList, setRantingList] = useState([]);
  const [postalList, setPostalList] = useState([]);
  const [docs, setDocs] = useState({});   // { ktp: {name, preview, isImage, data}, ... }
  const [submitting, setSubmitting] = useState(false);
  const [hydrated, setHydrated] = useState(false);   // true setelah draft dipulihkan

  // Kompres gambar via canvas agar payload kecil (maks sisi 1400px, JPEG 0.82).
  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1400;
        let { width, height } = img;
        if (width > max || height > max) {
          const ratio = Math.min(max / width, max / height);
          width = Math.round(width * ratio); height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  // Terima, validasi, kompres, & simpan dokumen (dengan data URL untuk dikirim ke server).
  const handleFile = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okType = /^(image\/(jpeg|png|webp)|application\/pdf)$/.test(file.type);
    if (!okType) { toast.error("Format harus JPG, PNG, WEBP, atau PDF"); e.target.value = ""; return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Ukuran file maksimal 5 MB"); e.target.value = ""; return; }
    const isImage = file.type.startsWith("image/");
    try {
      const data = isImage ? await compressImage(file) : await readAsDataUrl(file);
      setDocs((d) => ({ ...d, [key]: { name: file.name, isImage, preview: isImage ? data : null, data } }));
    } catch {
      toast.error("Gagal membaca file");
    }
    e.target.value = "";
  };
  const removeDoc = (key) => setDocs((d) => { const n = { ...d }; delete n[key]; return n; });

  // Metode pembayaran pendaftaran: Midtrans (bila aktif) atau transfer manual + bukti.
  const manualEnabled = !!koperasiInfo.bankAccount;
  const [payMethod, setPayMethod] = useState(manualEnabled ? "manual" : null);
  const [midtransEnabled, setMidtransEnabled] = useState(false);
  const [payCfgLoaded, setPayCfgLoaded] = useState(false);
  useEffect(() => {
    getMidtransConfig().then((cfg) => {
      if (cfg.enabled) { setMidtransEnabled(true); setPayMethod("midtrans"); }
      setPayCfgLoaded(true);
    });
  }, []);

  // Bukti transfer biaya pendaftaran.
  const [proof, setProof] = useState(null);
  const handleProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okType = /^(image\/(jpeg|png|webp)|application\/pdf)$/.test(file.type);
    if (!okType) { toast.error("Format harus JPG, PNG, WEBP, atau PDF"); e.target.value = ""; return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Ukuran file maksimal 5 MB"); e.target.value = ""; return; }
    const isImage = file.type.startsWith("image/");
    try {
      const data = isImage ? await compressImage(file) : await readAsDataUrl(file);
      setProof({ name: file.name, isImage, preview: isImage ? data : null, data });
    } catch { toast.error("Gagal membaca file"); }
    e.target.value = "";
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  // "none" adalah sentinel untuk pilihan "Tanpa Mitra" (Radix Select tak boleh value kosong).
  const setPartner = (val) => setForm((f) => ({ ...f, partner_organization_id: val === "none" ? "" : val }));

  // Provinsi → reset kota/cabang/ranting, ambil daftar kota.
  const setProvince = (val) => {
    setForm((f) => ({ ...f, province_id: val, regency_id: "", cabang_id: "", ranting_id: "", postal_code: "" }));
    setRegencies([]); setCabangList([]); setRantingList([]); setPostalList([]);
    PublicAPI.regencies(Number(val)).then(setRegencies).catch(() => setRegencies([]));
  };

  // Kota/Kabupaten → reset cabang/ranting/kode pos, ambil daftar Cabang & Kode Pos di kota tsb.
  const setRegency = (val) => {
    setForm((f) => ({ ...f, regency_id: val, cabang_id: "", ranting_id: "", postal_code: "" }));
    setCabangList([]); setRantingList([]); setPostalList([]);
    PublicAPI.cabang(Number(val)).then(setCabangList).catch(() => setCabangList([]));
    PublicAPI.postalCodes(Number(val)).then(setPostalList).catch(() => setPostalList([]));
  };

  // Cabang → reset ranting, ambil daftar Ranting di bawah cabang tsb.
  const setCabang = (val) => {
    setForm((f) => ({ ...f, cabang_id: val, ranting_id: "" }));
    setRantingList([]);
    PublicAPI.rantings(Number(val)).then(setRantingList).catch(() => setRantingList([]));
  };

  useEffect(() => {
    PublicAPI.partners().then(setPartners).catch(() => setPartners([]));
    PublicAPI.provinces().then(setProvinces).catch(() => setProvinces([]));
  }, []);

  // Pulihkan draft dari localStorage saat halaman dibuka (agar tak input ulang setelah refresh).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.form) setForm((f) => ({ ...f, ...s.form }));
        if (typeof s.step === "number") setStep(s.step);
        if (s.docs) setDocs(s.docs);
        const f = s.form || {};
        // Muat ulang daftar dependen agar dropdown menampilkan pilihan tersimpan.
        if (f.province_id) PublicAPI.regencies(Number(f.province_id)).then(setRegencies).catch(() => {});
        if (f.regency_id) PublicAPI.cabang(Number(f.regency_id)).then(setCabangList).catch(() => {});
        if (f.regency_id) PublicAPI.postalCodes(Number(f.regency_id)).then(setPostalList).catch(() => {});
        if (f.cabang_id) PublicAPI.rantings(Number(f.cabang_id)).then(setRantingList).catch(() => {});
        if (s.form?.name || s.form?.nik) toast.info("Draft pendaftaran dipulihkan");
      }
    } catch { /* abaikan draft rusak */ }
    setHydrated(true);
  }, []);

  // Simpan draft otomatis setiap ada perubahan (setelah dipulihkan).
  useEffect(() => {
    if (!hydrated) return;
    const payload = { form, step, docs };
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
    } catch {
      // Kemungkinan kuota penuh karena dokumen besar → simpan tanpa dokumen.
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, step, docs: {} })); } catch { /* menyerah */ }
    }
  }, [form, step, docs, hydrated]);

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setForm({ name: "", nik: "", email: "", phone_number: "", password: "", partner_organization_id: "", referral_code: "", province_id: "", regency_id: "", cabang_id: "", ranting_id: "", postal_code: "", place_of_birth: "", business_name: "" });
    setDocs({}); setStep(0);
    setRegencies([]); setCabangList([]); setRantingList([]); setPostalList([]);
    toast.success("Formulir dikosongkan");
  };

  const submitRegistration = async () => {
    const missing = [];
    if (!form.name) missing.push("Nama (langkah Identitas)");
    if (form.nik.length !== 16) missing.push("NIK 16 digit (langkah Identitas)");
    if (!form.phone_number) missing.push("No. HP (langkah Kontak)");
    if (!form.password) missing.push("Kata Sandi (langkah Kontak)");
    if (!payMethod) missing.push("Metode Pembayaran (langkah Pembayaran)");
    else if (payMethod === "manual" && !proof) missing.push("Bukti Transfer (langkah Pembayaran)");
    if (missing.length) {
      toast.error(`Lengkapi dulu: ${missing.join(", ")}.`);
      return;
    }
    setSubmitting(true);
    try {
      const data = await register({
        name: form.name,
        email: form.email || undefined,
        phone_number: form.phone_number,
        nik: form.nik,
        password: form.password,
        business_name: form.business_name || undefined,
        place_of_birth: form.place_of_birth || undefined,
        postal_code: form.postal_code || undefined,
        partner_organization_id: form.partner_organization_id ? Number(form.partner_organization_id) : undefined,
        referral_code: form.referral_code || undefined,
        // Relasi wilayah: pakai Ranting bila dipilih, jika tidak pakai Cabang.
        organization_id: form.ranting_id ? Number(form.ranting_id) : (form.cabang_id ? Number(form.cabang_id) : undefined),
        documents: {
          ktp: docs.ktp?.data,
          pasfoto: docs.pasfoto?.data,
          domisili: docs.domisili?.data,
        },
        payment_method: payMethod === "midtrans" ? "MIDTRANS" : "MANUAL_TRANSFER",
        payment_proof: payMethod === "manual" ? proof.data : undefined,
      });
      localStorage.removeItem(DRAFT_KEY); // draft selesai — bersihkan
      toast.success(`Pendaftaran terkirim! No. Anggota: ${data.member_number}`);
      if (data.affiliation) toast.success(`Terdaftar di ${data.affiliation.partner}: ${data.affiliation.member_number}`);
      if (payMethod === "midtrans") {
        // Bayar biaya pendaftaran sekarang. Bila belum selesai, bisa dilanjutkan dari halaman Masuk.
        if (data.payment?.error) toast.error(data.payment.error);
        else if (data.payment) await payRegistration(data.payment).catch((e) => toast.error(e.message));
        navigate("/masuk");
      } else {
        toast.info("Akun aktif setelah pembayaran diverifikasi Kantor Ranting. Silakan cek berkala lalu masuk.", { duration: 8000 });
        setTimeout(() => navigate("/masuk"), 1500);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  return (
    <div className="mx-auto max-w-6xl px-5 lg:px-10 py-10 lg:py-14">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <Sparkles size={16} className="text-amber-warm" />
          <span className="pill-tag bg-forest/10 text-forest">Pendaftaran Anggota Online</span>
        </div>
        <button type="button" onClick={clearDraft} className="text-xs text-ink/55 hover:text-terracotta underline" data-testid="reg-clear-draft">
          Kosongkan formulir
        </button>
      </div>
      <h1 className="font-display text-3xl lg:text-5xl font-bold text-ink tracking-tight">Selamat datang, calon anggota baru.</h1>
      <p className="text-ink/75 mt-3 max-w-xl">Proses 6 langkah · ±5 menit · verifikasi 1×24 jam kerja. <span className="text-forest">Isian tersimpan otomatis.</span></p>

      {/* Step indicator */}
      <div className="mt-10">
        <div className="flex items-center gap-1 overflow-x-auto pb-2">
          {steps.map((s, i) => (
            <div key={s.key} className="flex items-center gap-1 shrink-0">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${
                i < step ? "bg-forest text-white" : i === step ? "bg-amber-warm text-ink" : "bg-white border border-stone-soft text-ink/55"
              }`}>
                {i < step ? <Check size={13} /> : <span className="w-5 h-5 rounded-full bg-white/30 grid place-items-center text-[10px]">{i + 1}</span>}
                {s.label}
              </div>
              {i < steps.length - 1 && <div className={`w-4 h-px ${i < step ? "bg-forest" : "bg-stone-soft"}`} />}
            </div>
          ))}
        </div>
        <Progress value={((step + 1) / steps.length) * 100} className="mt-4 h-1.5" />
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 bg-white rounded-3xl border border-stone-soft p-7 lg:p-10 animate-fade-up" key={step}>
          {step === 0 && (
            <div className="space-y-5">
              <h2 className="font-display font-bold text-2xl text-ink">Data Identitas</h2>
              <p className="text-sm text-ink/75">Lengkapi sesuai KTP. Data Anda terlindungi UU PDP.</p>
              <div className="grid sm:grid-cols-2 gap-4 pt-4">
                <Field label="Nama Lengkap"><Input value={form.name} onChange={set("name")} placeholder="cth. Annisa Rahmawati" className="rounded-lg border-stone-soft" data-testid="reg-name" /></Field>
                <Field label="No. KTP / NIK"><Input value={form.nik} onChange={(e) => setForm((f) => ({ ...f, nik: e.target.value.replace(/\D/g, "") }))} placeholder="16 digit NIK" maxLength={16} className="rounded-lg border-stone-soft" data-testid="reg-nik" /></Field>
                <Field label="Tempat Lahir">
                  <Select value={form.place_of_birth} onValueChange={set("place_of_birth")}>
                    <SelectTrigger className="rounded-lg border-stone-soft" data-testid="reg-pob"><SelectValue placeholder="Pilih provinsi kelahiran..." /></SelectTrigger>
                    <SelectContent>
                      {provinces.map((p) => (
                        <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Tanggal Lahir"><Input type="date" className="rounded-lg border-stone-soft" data-testid="reg-dob" /></Field>
                <Field label="Jenis Kelamin">
                  <Select><SelectTrigger className="rounded-lg border-stone-soft"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                    <SelectContent><SelectItem value="L">Laki-laki</SelectItem><SelectItem value="P">Perempuan</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Status Perkawinan">
                  <Select><SelectTrigger className="rounded-lg border-stone-soft"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lajang">Belum Kawin</SelectItem>
                      <SelectItem value="kawin">Kawin</SelectItem>
                      <SelectItem value="cerai">Cerai</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <h2 className="font-display font-bold text-2xl text-ink">Data Kontak</h2>
              <div className="grid sm:grid-cols-2 gap-4 pt-2">
                <Field label="Email"><Input type="email" value={form.email} onChange={set("email")} placeholder="nama@email.com" className="rounded-lg border-stone-soft" data-testid="reg-email" /></Field>
                <Field label="No. Handphone (WhatsApp)"><Input value={form.phone_number} onChange={set("phone_number")} placeholder="0812..." className="rounded-lg border-stone-soft" data-testid="reg-phone" /></Field>
                <Field label="Kata Sandi"><Input type="password" value={form.password} onChange={set("password")} placeholder="Minimal 6 karakter" className="rounded-lg border-stone-soft" data-testid="reg-password" /></Field>
                <Field label="Program Mitra (opsional — SSO)">
                  <Select value={form.partner_organization_id || "none"} onValueChange={setPartner}>
                    <SelectTrigger className="rounded-lg border-stone-soft"><SelectValue placeholder="Tanpa Mitra" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Tanpa Mitra</SelectItem>
                      {partners.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>{p.organization_name}{p.program_name ? ` — ${p.program_name}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Kode Referral (opsional)"><Input value={form.referral_code} onChange={set("referral_code")} placeholder="cth. 46NKUGRT" className="rounded-lg border-stone-soft" data-testid="reg-referral" /></Field>
                <div className="sm:col-span-2"><Field label="Alamat Domisili"><Textarea rows={3} placeholder="Jalan, kelurahan, kecamatan..." className="rounded-lg border-stone-soft" data-testid="reg-address" /></Field></div>
                <Field label="Provinsi">
                  <Select value={form.province_id} onValueChange={setProvince}>
                    <SelectTrigger className="rounded-lg border-stone-soft" data-testid="reg-province"><SelectValue placeholder="Pilih provinsi..." /></SelectTrigger>
                    <SelectContent>
                      {provinces.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Kota / Kabupaten">
                  <Select value={form.regency_id} onValueChange={setRegency} disabled={!form.province_id}>
                    <SelectTrigger className="rounded-lg border-stone-soft" data-testid="reg-regency"><SelectValue placeholder={form.province_id ? "Pilih kota/kabupaten..." : "Pilih provinsi dulu"} /></SelectTrigger>
                    <SelectContent>
                      {regencies.map((r) => (
                        <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Kantor Cabang">
                  <Select value={form.cabang_id} onValueChange={setCabang} disabled={!form.regency_id}>
                    <SelectTrigger className="rounded-lg border-stone-soft" data-testid="reg-cabang">
                      <SelectValue placeholder={!form.regency_id ? "Pilih kota/kabupaten dulu" : cabangList.length ? "Pilih kantor cabang..." : "Belum ada cabang di kota ini"} />
                    </SelectTrigger>
                    <SelectContent>
                      {cabangList.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Kantor Ranting">
                  <Select value={form.ranting_id} onValueChange={set("ranting_id")} disabled={!form.cabang_id}>
                    <SelectTrigger className="rounded-lg border-stone-soft" data-testid="reg-ranting">
                      <SelectValue placeholder={!form.cabang_id ? "Pilih cabang dulu" : rantingList.length ? "Pilih kantor ranting..." : "Belum ada ranting di cabang ini"} />
                    </SelectTrigger>
                    <SelectContent>
                      {rantingList.map((r) => (
                        <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Kode Pos">
                  <Select value={form.postal_code} onValueChange={set("postal_code")} disabled={!form.regency_id}>
                    <SelectTrigger className="rounded-lg border-stone-soft" data-testid="reg-postal">
                      <SelectValue placeholder={!form.regency_id ? "Pilih kota/kabupaten dulu" : postalList.length ? "Pilih kode pos..." : "Belum ada data kode pos"} />
                    </SelectTrigger>
                    <SelectContent>
                      {postalList.map((p) => (
                        <SelectItem key={p.id} value={p.code}>{p.code}{p.district ? ` — ${p.district}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="font-display font-bold text-2xl text-ink">Data Pekerjaan</h2>
              <div className="grid sm:grid-cols-2 gap-4 pt-2">
                <Field label="Pekerjaan Utama">
                  <Select><SelectTrigger className="rounded-lg border-stone-soft"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pns">PNS</SelectItem>
                      <SelectItem value="swasta">Karyawan Swasta</SelectItem>
                      <SelectItem value="wira">Wiraswasta / UMKM</SelectItem>
                      <SelectItem value="petani">Petani / Peternak</SelectItem>
                      <SelectItem value="lainnya">Lainnya</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Nama Usaha / Instansi"><Input value={form.business_name} onChange={set("business_name")} placeholder="cth. Catering Annisa" className="rounded-lg border-stone-soft" data-testid="reg-org" /></Field>
                <Field label="Penghasilan Per Bulan">
                  <Select><SelectTrigger className="rounded-lg border-stone-soft"><SelectValue placeholder="Pilih kisaran..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="r1">&lt; Rp 3 juta</SelectItem>
                      <SelectItem value="r2">Rp 3 – 5 juta</SelectItem>
                      <SelectItem value="r3">Rp 5 – 10 juta</SelectItem>
                      <SelectItem value="r4">&gt; Rp 10 juta</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Lama Bekerja / Berusaha"><Input placeholder="cth. 5 tahun" className="rounded-lg border-stone-soft" /></Field>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="font-display font-bold text-2xl text-ink">Unggah Dokumen</h2>
              <p className="text-sm text-ink/75">Format JPG/PNG/PDF, maks. 5 MB per file.</p>
              <div className="grid sm:grid-cols-2 gap-4 pt-2">
                {[
                  { l: "Foto KTP", n: "ktp" },
                  { l: "Pas Foto 3×4", n: "pasfoto" },
                  { l: "Bukti Domisili (opsional)", n: "domisili" },
                ].map((d) => {
                  const doc = docs[d.n];
                  return doc ? (
                    <div key={d.n} className="border-2 border-forest/40 bg-forest/5 rounded-2xl p-4 relative" data-testid={`reg-doc-${d.n}`}>
                      <button type="button" onClick={() => removeDoc(d.n)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white border border-stone-soft grid place-items-center text-ink/55 hover:text-terracotta" data-testid={`reg-doc-remove-${d.n}`}>
                        <X size={14} />
                      </button>
                      {doc.isImage
                        ? <img src={doc.preview} alt={d.l} className="w-full h-28 object-cover rounded-lg" />
                        : <div className="h-28 grid place-items-center bg-white rounded-lg"><FileText size={30} className="text-forest" /></div>}
                      <div className="flex items-center gap-1.5 mt-2 text-forest"><Check size={14} /> <span className="font-semibold text-sm">{d.l}</span></div>
                      <div className="text-xs text-ink/55 mt-0.5 truncate">{doc.name}</div>
                    </div>
                  ) : (
                    <label key={d.n} htmlFor={`upload-${d.n}`} className="border-2 border-dashed border-stone-soft rounded-2xl p-6 text-center cursor-pointer hover:bg-cream transition" data-testid={`reg-upload-${d.n}`}>
                      <Upload size={22} className="mx-auto text-forest" />
                      <div className="font-semibold text-sm text-ink mt-2">{d.l}</div>
                      <div className="text-xs text-ink/55 mt-1">Klik untuk pilih file</div>
                      <input type="file" id={`upload-${d.n}`} accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={handleFile(d.n)} />
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <h2 className="font-display font-bold text-2xl text-ink">Pembayaran Awal</h2>
              <div className="rounded-2xl bg-cream p-5 space-y-2">
                <Row label="Simpanan Pokok (sekali)" value="Rp 200.000" />
                <Row label="Simpanan Wajib Bulan Pertama" value="Rp 100.000" />
                <div className="border-t border-stone-soft pt-2 mt-2 flex justify-between font-semibold text-ink">
                  <span>Total</span><span>Rp 300.000</span>
                </div>
              </div>
              <p className="text-xs text-ink/55">Seluruh pembayaran adalah simpanan milik Anda (tanpa biaya administrasi). Simpanan Wajib Rp 100.000 dibayarkan setiap bulan setelah menjadi anggota.</p>
              <div>
                <Label className="text-xs text-ink/55 uppercase tracking-wider mb-3 block">Metode Pembayaran</Label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    midtransEnabled && { v: "midtrans", icon: CreditCard, l: "Bayar Online (Midtrans)", d: "VA, QRIS, e-wallet, kartu — dibayar setelah formulir dikirim" },
                    manualEnabled && { v: "manual", icon: Banknote, l: "Transfer Manual", d: "Transfer ke rekening koperasi & unggah bukti" },
                  ].filter(Boolean).map((m) => (
                    <button
                      key={m.v}
                      type="button"
                      onClick={() => setPayMethod(m.v)}
                      className={`flex items-start gap-3 text-left border rounded-xl p-3 transition ${payMethod === m.v ? "border-forest bg-forest/5" : "border-stone-soft hover:bg-cream"}`}
                      data-testid={`reg-pay-${m.v}`}
                    >
                      <m.icon size={18} className="text-forest mt-0.5 shrink-0" />
                      <span>
                        <span className="block text-sm font-semibold text-ink">{m.l}</span>
                        <span className="block text-xs text-ink/65 mt-0.5">{m.d}</span>
                      </span>
                    </button>
                  ))}
                </div>
                {payCfgLoaded && !payMethod && (
                  <div className="mt-3 flex items-start gap-2 rounded-xl bg-terracotta/10 text-terracotta text-sm p-3">
                    <AlertCircle size={16} className="shrink-0 mt-0.5" /> Pembayaran belum tersedia. Silakan hubungi pengurus koperasi.
                  </div>
                )}
              </div>

              {payMethod === "midtrans" && (
                <p className="text-sm text-ink/75 rounded-2xl bg-forest/5 border border-forest/20 p-4">
                  Setelah menekan <b>Kirim Pendaftaran</b>, halaman pembayaran Midtrans akan terbuka. Pembayaran
                  tercatat otomatis — tidak perlu mengunggah bukti transfer.
                </p>
              )}

              {payMethod === "manual" && (<>
              {/* Rekening tujuan + unggah bukti transfer (wajib) */}
              <div className="rounded-2xl bg-cream p-5 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-ink/75">Bank Tujuan</span><span className="font-semibold text-ink">{koperasiInfo.bankName} {koperasiInfo.bankAccount}</span></div>
                <div className="flex justify-between"><span className="text-ink/75">Atas Nama</span><span className="font-semibold text-ink">{koperasiInfo.bankHolder}</span></div>
                <div className="flex justify-between"><span className="text-ink/75">Jumlah</span><span className="font-semibold text-forest">Rp 300.000</span></div>
              </div>
              <div>
                <Label className="text-xs text-ink/55 uppercase tracking-wider mb-2 block">Bukti Transfer (wajib)</Label>
                {proof ? (
                  <div className="border-2 border-forest/40 bg-forest/5 rounded-2xl p-4 relative">
                    <button type="button" onClick={() => setProof(null)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white border border-stone-soft grid place-items-center text-ink/55 hover:text-terracotta"><X size={14} /></button>
                    {proof.isImage ? <img src={proof.preview} alt="bukti" className="w-full h-40 object-contain rounded-lg bg-white" /> : <div className="h-28 grid place-items-center bg-white rounded-lg"><FileText size={30} className="text-forest" /></div>}
                    <div className="flex items-center gap-1.5 mt-2 text-forest"><Check size={14} /> <span className="text-sm truncate">{proof.name}</span></div>
                  </div>
                ) : (
                  <label htmlFor="upload-proof" className="block border-2 border-dashed border-stone-soft rounded-2xl p-6 text-center cursor-pointer hover:bg-cream transition" data-testid="reg-upload-proof">
                    <Upload size={22} className="mx-auto text-forest" />
                    <div className="font-semibold text-sm text-ink mt-2">Unggah bukti transfer</div>
                    <div className="text-xs text-ink/55 mt-1">JPG/PNG/WEBP/PDF, maks. 5 MB</div>
                    <input type="file" id="upload-proof" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={handleProof} />
                  </label>
                )}
              </div>
              </>)}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <h2 className="font-display font-bold text-2xl text-ink">Review & Persetujuan</h2>
              <div className="rounded-2xl bg-cream p-5 text-sm space-y-2">
                <Row label="Nama" value={form.name || "—"} />
                <Row label="NIK" value={form.nik ? form.nik.replace(/(\d{4})(?=\d)/g, "$1 ").trim() : "—"} />
                <Row label="No. HP" value={form.phone_number || "—"} />
                <Row label="Program Mitra" value={partners.find((p) => String(p.id) === form.partner_organization_id)?.organization_name || "Tanpa mitra"} />
                <Row label="Total Pembayaran" value="Rp 300.000" />
              </div>
              <div className="flex items-start gap-3">
                <Checkbox id="agree" defaultChecked data-testid="reg-agree" />
                <Label htmlFor="agree" className="text-sm text-ink/75 cursor-pointer leading-relaxed">
                  Saya menyetujui AD/ART Koperasi Andalan Dapur Nusantara,{" "}
                  <Link to="/syarat-ketentuan" target="_blank" className="text-forest underline">Syarat & Ketentuan</Link>,{" "}
                  <Link to="/kebijakan-privasi" target="_blank" className="text-forest underline">Kebijakan Privasi</Link>, dan
                  pernyataan kebenaran data. Saya bersedia mengikuti Rapat Anggota Tahunan.
                </Label>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-10">
            <Button variant="outline" onClick={prev} disabled={step === 0} className="rounded-full" data-testid="reg-prev">
              <ChevronLeft size={16} /> Sebelumnya
            </Button>
            {step < steps.length - 1 ? (
              <Button onClick={next} className="rounded-full bg-forest hover:bg-forest-dark text-white" data-testid="reg-next">
                Lanjut <ChevronRight size={16} />
              </Button>
            ) : (
              <Button
                onClick={submitRegistration}
                disabled={submitting}
                className="rounded-full bg-amber-warm text-ink hover:bg-amber-warm/90 font-semibold"
                data-testid="reg-submit"
              >
                {submitting ? "Mengirim..." : payMethod === "midtrans" ? "Kirim & Bayar" : "Kirim Pendaftaran"} <Check size={16} />
              </Button>
            )}
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-4">
          <div className="rounded-3xl bg-forest text-cream p-6">
            <div className="text-xs uppercase tracking-[0.22em] text-amber-warm font-semibold">Sudah punya akun?</div>
            <h3 className="font-display font-semibold text-xl mt-2">Masuk ke Ruang Anggota</h3>
            <Link to="/masuk"><Button className="mt-4 rounded-full bg-amber-warm text-ink hover:bg-amber-warm/90 w-full">Masuk Sekarang</Button></Link>
          </div>
          <div className="rounded-3xl bg-white border border-stone-soft p-6">
            <ShieldCheck className="text-forest" size={22} />
            <h3 className="font-display font-semibold text-base text-ink mt-2">Data Anda Aman</h3>
            <p className="text-xs text-ink/75 mt-2 leading-relaxed">
              Semua dokumen disimpan terenkripsi (AES-256), dikelola oleh staf bersertifikasi, dan tidak dibagikan ke pihak ketiga tanpa izin Anda.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-ink/55 uppercase tracking-wider">{label}</Label>
    {children}
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between text-sm"><span className="text-ink/75">{label}</span><span className="font-semibold text-ink">{value}</span></div>
);
